import { useConfigurableAction } from "@shesha-io/reactjs";
import { ESIGN_SIGNING_ACTIONS, ESIGN_SIGNING_ACTIONS_OWNER } from "./model";

// ─── Shared types ─────────────────────────────────────────────────────────────

interface CloseMessage {
    type: 'CLOSE_VIEWER' | 'SIGNING_COMPLETE' | 'SETUP_COMPLETE';
}

// ─── Shared modal helpers ─────────────────────────────────────────────────────

function buildModal(overlayId: string, loadingText: string) {
    const overlay = document.createElement('div');
    overlay.id = overlayId;
    overlay.style.cssText = `
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background-color: rgba(0, 0, 0, 0.45);
        z-index: 1000;
        display: flex; align-items: center; justify-content: center;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
        position: relative;
        width: 100%; height: 100%;
        max-width: 100vw; max-height: 100vh;
        background: white;
        display: flex; flex-direction: column;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    const iframeContainer = document.createElement('div');
    iframeContainer.style.cssText = `flex: 1; position: relative; overflow: hidden; height: 100%;`;

    const loadingDiv = document.createElement('div');
    loadingDiv.style.cssText = `
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        font-size: 16px; color: rgba(0, 0, 0, 0.65); z-index: 10;
    `;
    loadingDiv.textContent = loadingText;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = `width: 100%; height: 100%; border: none; display: none;`;

    iframe.onload = () => {
        loadingDiv.style.display = 'none';
        iframe.style.display = 'block';
    };

    iframeContainer.appendChild(loadingDiv);
    iframeContainer.appendChild(iframe);
    modal.appendChild(iframeContainer);
    overlay.appendChild(modal);

    return { overlay, iframe };
}

function mountModal(
    overlay: HTMLElement,
    overlayId: string,
    closeOn: string[],
    onClose?: () => void
) {
    const previousBodyOverflow = document.body.style.overflow;

    const closeModal = (): void => {
        if (!document.getElementById(overlayId)) return;
        document.body.style.overflow = previousBodyOverflow;
        overlay.remove();
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('message', handleMessage);
        onClose?.();
    };

    const handleMessage = (event: MessageEvent<string | CloseMessage>): void => {
        try {
            const data = event.data;
            const type = typeof data === 'string' ? data : (data as CloseMessage)?.type;
            if (closeOn.includes(type)) closeModal();
        } catch {
            // ignore
        }
    };

    const handleEscape = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') closeModal();
    };

    overlay.onclick = (e: MouseEvent): void => {
        if (e.target === overlay) closeModal();
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('keydown', handleEscape);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
}

// ─── Action: Open ESign Field Setup (initiator) ───────────────────────────────

interface FileContext {
    fileId: string;
}

interface Contexts {
    currentFile?: FileContext;
}

interface User {
    id: string;
}

interface Application {
    user: User;
}

interface HttpResponse<T> {
    data: {
        result: T;
    };
}

interface Http {
    post<T = unknown>(url: string): Promise<HttpResponse<T>>;
}

interface ActionContext {
    contexts?: Contexts;
    http: Http;
    application: Application;
}

export const useESignFieldSetupAction = () => {
    useConfigurableAction({
        owner:      ESIGN_SIGNING_ACTIONS_OWNER,
        ownerUid:   ESIGN_SIGNING_ACTIONS_OWNER,
        name:       ESIGN_SIGNING_ACTIONS.OPEN_ESIGN_FIELD_SETUP,
        hasArguments: true,
        // isPermament prevents the cleanup from unregistering the action on unmount.
        // Without this flag, React Strict Mode's double-invoke cycle briefly removes the
        // action from the registry between cleanup and re-registration, causing
        // "Action not found" errors on button click.
        isPermament: true,
        executer: async (_actionArgs, context) => {
            const { contexts, application } = context as unknown as ActionContext;
            const storedFileId = contexts?.currentFile?.fileId;
            const userId = application?.user?.id;

            if (!storedFileId) {
                console.error('[ESign] No storedFileId in context for field setup');
                throw new Error('storedFileId is required to open the field setup viewer');
            }

            const url = `/esign-viewer/${encodeURIComponent(storedFileId)}?mode=setup&userId=${encodeURIComponent(userId ?? '')}`;
            const { overlay, iframe } = buildModal('esign-setup-overlay', 'Loading document…');
            iframe.src = url;

            mountModal(overlay, 'esign-setup-overlay', [
                'SETUP_COMPLETE',
                'CLOSE_VIEWER',
            ]);
        },
    }, []);
};

// ─── Action: Open ESign Signing (signatory) ───────────────────────────────────

export const useESignSigningAction = () => {
    useConfigurableAction({
        owner:      ESIGN_SIGNING_ACTIONS_OWNER,
        ownerUid:   ESIGN_SIGNING_ACTIONS_OWNER,
        name:       ESIGN_SIGNING_ACTIONS.OPEN_ESIGN_SIGNING,
        hasArguments: true,
        isPermament: true,
        executer: async (_actionArgs, context) => {
            const { application } = context as unknown as ActionContext;

            const urlParams = new URLSearchParams(window.location.search);
            const todoId = urlParams.get('todoid');
            const userId = application?.user?.id;

            if (!todoId) {
                console.error('[ESign] No todoId in URL for signing');
                throw new Error('todoId is required to open the ESign signing viewer');
            }

            const url = `/esign-viewer/${encodeURIComponent(todoId)}?mode=sign&userId=${encodeURIComponent(userId ?? '')}`;
            const { overlay, iframe } = buildModal('esign-signing-overlay', 'Loading signing viewer…');
            iframe.src = url;

            mountModal(overlay, 'esign-signing-overlay', [
                'SIGNING_COMPLETE',
                'CLOSE_VIEWER',
            ]);
        },
    }, []);
};
